import { createContext, useContext, useEffect, useState } from "react";
import { effectivePrice, applicableOffer, computeLineOffer } from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "sla_cart";

const keyFor = (productId, unit) => `${productId}::${unit || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (_) {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // option: { unit: string|null, label: string, rate: number } — falls back to base price
  const addItem = (product, qty = 1, option = null) => {
    const unit = option ? option.unit : null;
    const unitLabel = option ? option.label : product.pack_size || "Each";
    const rate = option ? option.rate : Number(effectivePrice(product)) || 0;
    const key = keyFor(product.id, unit);
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [...prev, { key, product, qty, unit, unitLabel, rate }];
    });
  };

  const updateQty = (key, qty) => {
    if (qty <= 0) return removeItem(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const clear = () => setItems([]);

  // Per-line offer computation (cash discount + free items), scaling with quantity.
  const lineInfo = (i) => {
    const base = (Number(i.rate) || 0) * i.qty;
    const off = computeLineOffer(applicableOffer(i.product), i.unit, i.qty);
    const discount = off ? Math.min(base, off.cashOff) : 0;
    const free = off && off.freeQty > 0 && off.freeItem
      ? { qty: off.freeQty, unit: off.freeUnit, name: off.freeItem }
      : null;
    return { base, discount, net: base - discount, free };
  };

  const count = items.reduce((sum, i) => sum + i.qty, 0);
  const subtotal = items.reduce((sum, i) => sum + lineInfo(i).base, 0);
  const discount = items.reduce((sum, i) => sum + lineInfo(i).discount, 0);
  const total = subtotal - discount;
  const freeItems = items.map(lineInfo).map((li) => li.free).filter(Boolean);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, count, total, subtotal, discount, freeItems, lineInfo }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
